import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { BiSitemap } from 'react-icons/bi';
import PageHeader from 'Utils/PageHeader';
import FetchContainer from 'Utils/FetchContainer';
import AppContext from 'Utils/AppContext';
import { getMaskingJobGroup, replaceMaskingJobGroupSteps } from 'Secure/api';
import StepComposer from './StepComposer';
import Breadcrumbs from 'Utils/Breadcrumbs';

export const GROUPS_PATH = '/secure/groups';

/** Route to the add-step page of a group; `editSteps` (one block's steps) opens it in edit mode. */
export function stepPagePath(groupId, editSteps) {
  const base = `${GROUPS_PATH}/${encodeURIComponent(groupId)}/step`;
  const ids = (editSteps || []).map((s) => s.id).filter((id) => id != null);
  return ids.length > 0 ? `${base}?edit=${ids.join(',')}` : base;
}

/** Step ids named by `?edit=1,2,3`, as numbers; empty for the add flow. */
export function editStepIdsOf(search) {
  const raw = new URLSearchParams(search || '').get('edit');
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n));
}

// Add / edit a masking job of an Orchestration Group as a full page (/secure/groups/:groupId/step,
// `?edit=<stepIds>` for an existing block): the pickers sit on the left and the destination
// review fills the right half live as destinations are picked. The page loads the group itself
// (the list page's copy may be stale), splices the emitted steps into the group's step list —
// appended for an add, replacing the edited block in place for an edit — and returns to the
// list with that group expanded.
function AddStepPage() {
  const { groupId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { setNotification } = useContext(AppContext);
  const [group, setGroup] = useState(null);
  const [fetchingData, setFetchingData] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const editIds = useMemo(() => editStepIdsOf(location.search), [location.search]);

  useEffect(() => {
    let cancelled = false;
    setFetchingData(true);
    getMaskingJobGroup(groupId)
      .then((g) => {
        if (cancelled) return;
        setGroup(g && typeof g === 'object' ? g : null);
        setError(g && typeof g === 'object' ? null : new Error('Group not found.'));
      })
      .catch((e) => {
        if (!cancelled) setError(e);
      })
      .finally(() => {
        if (!cancelled) setFetchingData(false);
      });
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  // The block being edited, in the group's current step order. Stable identity: the composer
  // re-seeds whenever this changes.
  const editSteps = useMemo(() => {
    if (!group || editIds.length === 0) return undefined;
    const steps = (group.steps || []).filter((s) => s && editIds.includes(s.id));
    return steps.length > 0 ? steps : undefined;
  }, [group, editIds]);
  const editing = !!editSteps;
  const groupName = group ? group.name : '';

  const backToGroups = useCallback(
    () => history.push(GROUPS_PATH, group ? { expandGroupId: group.id } : undefined),
    [history, group]
  );

  // StepComposer emits an ARRAY of steps (one per chosen destination; single in-place step
  // when none) so a fan-out extension lands in one save. In edit mode the emitted steps
  // replace the edited block in place (same position in the run order).
  const handleSubmit = async (steps) => {
    if (!group) return;
    const list = Array.isArray(steps) ? steps : [steps];
    const existing = group.steps || [];
    let next;
    if (editSteps) {
      const at = existing.indexOf(editSteps[0]);
      const remaining = existing.filter((s) => !editSteps.includes(s));
      const insertAt = at < 0 ? remaining.length : Math.min(at, remaining.length);
      remaining.splice(insertAt, 0, ...list);
      next = remaining;
    } else {
      next = [...existing, ...list];
    }
    setSubmitting(true);
    try {
      await replaceMaskingJobGroupSteps(
        group.id,
        next.map((s) => ({
          dataSourceId: s.dataSourceId,
          inventoryId: s.inventoryId,
          targetDataSourceId: s.targetDataSourceId != null ? s.targetDataSourceId : undefined,
        }))
      );
      backToGroups();
    } catch (e) {
      setSubmitting(false);
      const body =
        (e && e.response && e.response.data && e.response.data.message) ||
        (e && e.body && typeof e.body === 'object' && e.body.message) ||
        (e && e.message) ||
        'Unexpected error.';
      setNotification({ header: 'Step update failed', body });
    }
  };

  return (
    <div>
      <FetchContainer error={error} fetchingData={fetchingData} absolute={true} fluid>
        <Breadcrumbs
          className='mt-3'
          items={[
            { label: 'Orchestration Groups', to: { pathname: GROUPS_PATH, state: group ? { expandGroupId: group.id } : undefined }, testId: 'job-group-step-back' },
            { label: groupName || 'Group' },
          ]}
        />
        <PageHeader
          header={editing ? 'Edit masking job' : 'Add masking job'}
          icon={BiSitemap}
          tooltip='A masking job is a data source, one of its saved inventories, and any number of destinations. Several destinations run as one fan-out.'
          meta={
            group && group.runActive ? (
              <span className='sg-small' style={{ color: 'var(--status-warning-text, #B45309)' }}>
                This group is running. Its steps can be changed once the run finishes.
              </span>
            ) : undefined
          }
        />
        {group && editIds.length > 0 && !editSteps && (
          <div className='alert alert-warning' role='alert' data-testid='job-group-step-missing'>
            The steps you were editing are no longer in this group. The pickers below add a new job instead.
          </div>
        )}
        {group && (
          <StepComposer
            initialSteps={editSteps}
            submitting={submitting}
            locked={!!group.runActive}
            onSubmit={handleSubmit}
            onCancel={backToGroups}
          />
        )}
      </FetchContainer>
    </div>
  );
}

export default AddStepPage;
